import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  User, Mail, Phone, Globe, Camera, Save, Loader2,
  Instagram, Facebook, Twitter, Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    website: "",
    bio: "",
    business_name: "",
    social_links: {
      instagram: "",
      facebook: "",
      twitter: ""
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/admin/profile`, { withCredentials: true });
      setProfile({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        website: response.data.website || "",
        bio: response.data.bio || "",
        business_name: response.data.business_name || "",
        social_links: {
          instagram: response.data.social_links?.instagram || "",
          facebook: response.data.social_links?.facebook || "",
          twitter: response.data.social_links?.twitter || ""
        }
      });
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/profile`, profile, { withCredentials: true });
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSocialChange = (platform, value) => {
    setProfile(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-profile">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Admin Profile</h1>
        <p className="text-muted-foreground">
          Manage your admin account and public contact information
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Basic Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-xl font-serif flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-2 block">Name</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  data-testid="admin-name"
                />
              </div>

              <div>
                <Label className="mb-2 block">Business Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={profile.business_name}
                    onChange={(e) => setProfile(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="360Lumina Photography"
                    className="pl-10"
                    data-testid="admin-business-name"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Displayed on the main website contact section
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Bio</Label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell visitors about your business..."
                  rows={4}
                  data-testid="admin-bio"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-xl font-serif flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-2 block">Contact Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={profile.contact_email || ""}
                    onChange={(e) => setProfile(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="contact@example.com"
                    className="pl-10"
                    data-testid="admin-contact-email"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  This email is displayed publicly. Your login email ({profile.login_email}) remains unchanged.
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                    className="pl-10"
                    data-testid="admin-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="url"
                    value={profile.website}
                    onChange={(e) => setProfile(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://example.com"
                    className="pl-10"
                    data-testid="admin-website"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-xl font-serif flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Social Media Links
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="mb-2 block">Instagram</Label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={profile.social_links.instagram}
                      onChange={(e) => handleSocialChange("instagram", e.target.value)}
                      placeholder="https://instagram.com/..."
                      className="pl-10"
                      data-testid="admin-instagram"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Facebook</Label>
                  <div className="relative">
                    <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={profile.social_links.facebook}
                      onChange={(e) => handleSocialChange("facebook", e.target.value)}
                      placeholder="https://facebook.com/..."
                      className="pl-10"
                      data-testid="admin-facebook"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Twitter / X</Label>
                  <div className="relative">
                    <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={profile.social_links.twitter}
                      onChange={(e) => handleSocialChange("twitter", e.target.value)}
                      placeholder="https://twitter.com/..."
                      className="pl-10"
                      data-testid="admin-twitter"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="btn-primary rounded-sm"
          data-testid="save-profile"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminProfile;
